import * as React from 'react'
import {Typography, Grid} from '@mui/material'
import {makeStyles} from '@oc/styles'
import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import {observer} from 'mobx-react-lite'
import {useIsLoading} from '@oc/shell-core'
import {useCustomerRouteContext} from '@oc/shell-core'

const useStyles = makeStyles(theme => ({
    header: {
        ...(theme.mixins.toolbar as React.CSSProperties),
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(2),
        boxSizing: 'border-box'
    },
    wrapperWithActions: {
        padding: `${theme.spacing(1)} ${theme.spacing(2)} calc(${theme.spacing(4)} + 56px) ${theme.spacing(2)}`
    },
    wrapper: {
        padding: theme.spacing(1, 2)
    },
    root: {
        flex: '1 1 0',
        position: 'relative',
        height: '100%',
        backgroundColor: '#FAFAFA',
        overflow: 'auto'
    },
    bottomNavigation: {
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: 'auto',
        maxWidth: 'calc(100vw - 240px)',
        transition: 'max-width 1s',
        boxShadow: theme.shadows[6],
        margin: theme.spacing(0, 2, 2, 0),
        zIndex: 100,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0, 2)
    },
    action: {
        flexDirection: 'row'
    },
    disabledAction: {
        color: '#bababa'
    },
    actionLabel: {
        marginLeft: theme.spacing(1)
    }
}))

/**
 * @deprecated This should not be used anymore if you are using Layout from @oc/shell-core, since Layout handles bottom actions itself
 */
export interface BottomAction {
    label: string
    icon: React.ComponentType
    callback: () => void
    isDisabled?: () => boolean
    key: string | number
    primary?: boolean
}

/**
 * @deprecated Use same interface from @oc/shell-core if you are using Layout from @oc/shell-core
 */
export interface LayoutProps {
    children?: React.ReactNode
    header: string
    actions?: BottomAction[]
}

interface ActionsProps {
    actions?: BottomAction[]
}

function _Actions({actions}: ActionsProps) {
    const {classes, cx} = useStyles()

    if (!actions) {
        return null
    }

    const firstPrimary = actions.findIndex(x => x.primary && !(x.isDisabled && x.isDisabled()))

    return (
        <BottomNavigation id="navigation-action-bar" className={classes.bottomNavigation} value={firstPrimary}>
            {actions.map(({label, icon: Icon, callback, isDisabled, key}) => {
                const disabled = isDisabled && isDisabled()

                return (
                    <BottomNavigationAction
                        key={key}
                        disabled={disabled}
                        label={label}
                        icon={<Icon />}
                        onClick={callback}
                        classes={{
                            root: cx(classes.action, {
                                [classes.disabledAction]: disabled
                            }),
                            label: classes.actionLabel
                        }}
                        showLabel
                    />
                )
            })}
        </BottomNavigation>
    )
}
/**
 * @deprecated This should not be used anymore if you are using Layout from @oc/shell-core, since Layout handles bottom actions itself
 */
export const Actions = observer(_Actions)

function Layout({children, header, actions}: LayoutProps) {
    const {classes} = useStyles()

    React.useEffect(
        function () {
            document.title = `${header} | Operations Console`
        },
        [header]
    )

    return (
        <NoScrollWhenLoading>
            <Grid container>
                <Grid item xs={12}>
                    <Typography variant="h5" className={classes.header}>
                        {header}
                    </Typography>
                </Grid>

                <Grid xs={12} className={actions ? classes.wrapperWithActions : classes.wrapper} item>
                    {children || null}
                </Grid>
            </Grid>

            <Actions actions={actions} />
        </NoScrollWhenLoading>
    )
}

function NoScrollWhenLoading({children}: React.PropsWithChildren) {
    const isLoading = useIsLoading()
    const {classes} = useStyles()

    return (
        <div id="layout-id" className={classes.root} style={{overflow: isLoading ? 'hidden' : 'auto'}}>
            {children}
        </div>
    )
}

/**
 * @deprecated Use same function from @oc/shell-core if you are using Layout from @oc/shell-core
 */
export function useFormattedHeader(base: string, variant: 'customer' | 'rk' | 'base'): string {
    const {accountName, rkName, loading} = useCustomerRouteContext()

    let val

    switch (variant) {
        case 'customer':
            val = accountName
            break
        case 'rk':
            val = rkName
            break
        default:
            return base
    }

    return loading ? `${base} (loading...)` : `${base} (${val})`
}

/**
 * @deprecated Consider using same component from @oc/shell-core (for new UX design)
 */
export default Layout
